import type { jsPDF } from 'jspdf';
import {colors, fonts, layout, RGB} from '../../theme/themes'
import { IconDrawFn } from '../../utils/summary_report/icon';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function setFill(doc: jsPDF, rgb: RGB) {
  doc.setFillColor(rgb[0], rgb[1], rgb[2]);
}
function setDraw(doc: jsPDF, rgb: RGB) {
  doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
}
function setText(doc: jsPDF, rgb: RGB) {
  doc.setTextColor(rgb[0], rgb[1], rgb[2]);
}

/**
 * Card-level title text (e.g. "Households, last 5 months (bar)"). jsPDF's
 * font/size/color are global mutable state, not scoped per draw call, so
 * this always sets all three explicitly rather than relying on whatever
 * the previous primitive happened to leave behind.
 */
export function drawCardTitle(doc: jsPDF, x: number, y: number, text: string): void {
  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(9);
  setText(doc, colors.stone500);
  doc.text(text, x, y);
}

/**
 * If drawing something `neededHeight` tall at the current y would run
 * past the page, starts a new page and returns the reset y. Otherwise
 * returns y unchanged. jsPDF does not auto-paginate — without this, a
 * section that grows (more risk districts, a longer catchment list) can
 * silently draw part of itself past the physical page edge, where it's
 * simply never visible. Call this with a section's total computed height
 * BEFORE drawing anything in it, so the whole section — header included —
 * moves to the new page together rather than leaving an orphaned header
 * at the bottom of the previous one.
 */
/**
 * Extra top space every new page should reserve, set once per report run
 * by `buildEvictionReport` right after it measures the header's actual
 * height on page 1 (see `repeatedHeaderHeight` there). Stays 0 for any
 * caller (tests, other reports) that never sets it, so this is additive
 * and doesn't change existing single-header behavior by default.
 */
export let repeatedHeaderHeight = 0;

export function setRepeatedHeaderHeight(height: number): void {
  repeatedHeaderHeight = height;
}

export function ensureSpace(doc: jsPDF, y: number, neededHeight: number, marginTop = 40): number {
  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomMargin = 32;
  if (y + neededHeight > pageHeight - bottomMargin) {
    doc.addPage();
    return marginTop + repeatedHeaderHeight;
  }
  return y;
}

/** Section header: "01 · Eviction numbers" in accent orange, plus a muted subtitle. */
export function drawSectionHeader(
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  index: string,
  title: string,
  subtitle: string,
): number {
  doc.setFont(fonts.body, 'bold');
  doc.setFontSize(layout.sectionLabelSize);
  setText(doc, colors.accent);
  doc.text(`${index} · ${title}`.toUpperCase(), x, y);

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(layout.sectionSubLabelSize);
  setText(doc, colors.stone400);
  doc.text(subtitle, x, y + 11);

  return y + 20; // next y cursor
}

/** A bordered stat card: label, a hero YTD number, an optional MTD line, optional trend badge. */
export interface MetricCardOptions {
  rect: Rect;
  label: string;
  ytdValue: string;
  mtdValue?: string;
  trendPct?: number | null; // positive = up (shown red), negative = down (shown green)
  fillColor?: RGB; // defaults to white with a stone border
  /** Vector-drawn icon (small, inline with the label). Prefer iconImage for real artwork. */
  icon?: IconDrawFn;
  iconColor?: RGB; // defaults to stone500 — only used with the vector `icon`
  /** Real icon image as a base64 data URI — rendered as a left-side badge,
   * vertically centered against the whole card, with all text shifted
   * right to make room. Takes priority over `icon` if both are given. */
  iconImage?: string;
}

const METRIC_CARD_ICON_IMAGE_SIZE = 36;
const METRIC_CARD_ICON_IMAGE_GAP = 12;

export function drawMetricCard(doc: jsPDF, opts: MetricCardOptions): void {
  const { rect, label, ytdValue, mtdValue, trendPct, fillColor, icon, iconColor, iconImage } = opts;

  setFill(doc, fillColor ?? colors.white);
  setDraw(doc, colors.stone200);
  doc.setLineWidth(0.5);
  doc.roundedRect(rect.x, rect.y, rect.width, rect.height, layout.cardRadius, layout.cardRadius, fillColor ? 'F' : 'FD');

  let padX = rect.x + 14;
  let cursorY = rect.y + 20;

  if (iconImage) {
  // Align with the label row (same vertical rhythm as the old vector icon)
  const iconY = rect.y + 12;                     // top padding
  doc.addImage(
    iconImage,
    padX,
    iconY,
    METRIC_CARD_ICON_IMAGE_SIZE,
    METRIC_CARD_ICON_IMAGE_SIZE,
  );
  padX = padX + METRIC_CARD_ICON_IMAGE_SIZE + METRIC_CARD_ICON_IMAGE_GAP;
}

  let labelX = padX;
  if (icon && !iconImage) {
    const iconSize = 11;
    icon(doc, padX, cursorY - 9, iconSize, iconColor ?? colors.stone500);
    labelX = padX + iconSize + 5;
  }

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(9);
  setText(doc, colors.stone500);
  doc.text(label, labelX, cursorY);

  cursorY += 16;
  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(7.5);
  setText(doc, colors.stone400);
  doc.text('YEAR TO DATE', padX, cursorY);

  cursorY += 19;
  doc.setFont(fonts.mono, 'bold');
  doc.setFontSize(20);
  setText(doc, colors.stone900);
  doc.text(ytdValue, padX, cursorY);

  if (trendPct !== undefined && trendPct !== null) {
    const isUp = trendPct >= 0;
    const trendColor = isUp ? colors.red700 : colors.green700;
    setText(doc, trendColor);
    doc.setFont(fonts.body, 'bold');
    doc.setFontSize(9);
    const pctLabel = `${Math.abs(trendPct).toFixed(1)}%`;
    const pctWidth = doc.getTextWidth(pctLabel);
    const arrowSize = 3;
    const textBaselineY = cursorY - 5;
    const textX = rect.x + rect.width - 14 - pctWidth;
    const arrowX = textX - 7;

    // Core PDF fonts (Helvetica/Times/Courier) use WinAnsi encoding and have
    // no ▲/▼ glyphs — draw the arrow as a filled triangle instead of text
    // so it renders correctly regardless of which font is active.
    setFill(doc, trendColor);
    if (isUp) {
      doc.triangle(arrowX, textBaselineY + 1, arrowX + arrowSize * 1.8, textBaselineY + 1, arrowX + arrowSize * 0.9, textBaselineY - arrowSize * 1.4, 'F');
    } else {
      doc.triangle(arrowX, textBaselineY - arrowSize * 1.2, arrowX + arrowSize * 1.8, textBaselineY - arrowSize * 1.2, arrowX + arrowSize * 0.9, textBaselineY + 1.2, 'F');
    }
    doc.text(pctLabel, textX, textBaselineY);
  }

  if (mtdValue !== undefined) {
    cursorY += 16;
    setDraw(doc, colors.stone200);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(padX, cursorY, rect.x + rect.width - 14, cursorY);
    doc.setLineDashPattern([], 0);

    cursorY += 15;
    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(7.5);
    setText(doc, colors.stone400);
    doc.text('THIS MONTH', padX, cursorY);

    cursorY += 16;
    doc.setFont(fonts.mono, 'bold');
    doc.setFontSize(13);
    setText(doc, colors.stone900);
    doc.text(mtdValue, padX, cursorY);
  }
  // cursorY now sits at the last text baseline; callers size rect.height
  // to leave real breathing room below it before the card's bottom border.
}

/** Vertical bar chart. `highlight[i]=true` bars render in full accent color (e.g. peak months). */
export interface BarChartOptions {
  rect: Rect;
  values: number[];
  labels: string[]; // shown only at first/last position to avoid crowding
  highlight?: boolean[];
}

export function drawBarChart(doc: jsPDF, opts: BarChartOptions): void {
  const { rect, values, labels, highlight } = opts;
  const max = Math.max(...values, 1);
  const barGap = 3;
  const barWidth = (rect.width - barGap * (values.length - 1)) / values.length;
  const baselineY = rect.y + rect.height - 10;
  const maxBarHeight = rect.height - 14;

  setDraw(doc, colors.stone200);
  doc.setLineWidth(0.5);
  doc.line(rect.x, baselineY, rect.x + rect.width, baselineY);

  values.forEach((v, i) => {
    const barHeight = (v / max) * maxBarHeight;
    const x = rect.x + i * (barWidth + barGap);
    const isHighlighted = highlight?.[i] ?? false;
    setFill(doc, isHighlighted ? colors.accent : colors.accentLight);
    doc.roundedRect(x, baselineY - barHeight, barWidth, barHeight, 1, 1, 'F');
  });

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(6.5);
  setText(doc, colors.stone400);
  if (labels.length === 1) {
    doc.text(labels[0], rect.x, baselineY + 9);
  } else {
    doc.text(labels[0], rect.x, baselineY + 9);
    const lastLabel = labels[labels.length - 1];
    doc.text(lastLabel, rect.x + rect.width - doc.getTextWidth(lastLabel), baselineY + 9);
  }
}

/** Line chart with a marker dot at every point; `markIndex` gets a larger accent-colored dot. */
export interface LineChartOptions {
  rect: Rect;
  values: number[];
  labels: string[];
  markIndex?: number; // e.g. the lowest month
}

export function drawLineChart(doc: jsPDF, opts: LineChartOptions): void {
  const { rect, values, labels, markIndex } = opts;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const baselineY = rect.y + rect.height - 10;
  const plotHeight = rect.height - 14;
  // A single data point (e.g. only January selected) has nothing to space
  // against — dividing by (length - 1) there gives Infinity/NaN, which
  // jsPDF's circle() rejects outright. Guard it and center the lone point.
  const stepX = values.length > 1 ? rect.width / (values.length - 1) : 0;
  const singlePointX = rect.x + rect.width / 2;

  setDraw(doc, colors.stone200);
  doc.setLineWidth(0.5);
  doc.line(rect.x, baselineY, rect.x + rect.width, baselineY);

  const points = values.map((v, i) => ({
    x: values.length > 1 ? rect.x + i * stepX : singlePointX,
    y: baselineY - ((v - min) / range) * plotHeight,
  }));

  setDraw(doc, colors.stone500);
  doc.setLineWidth(2.2);
  for (let i = 0; i < points.length - 1; i++) {
    doc.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
  }

  points.forEach((p, i) => {
    const isMarked = i === markIndex;
    setFill(doc, isMarked ? colors.accent : colors.stone500);
    doc.circle(p.x, p.y, isMarked ? 1.6 : 1.1, 'F');
  });

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(6.5);
  setText(doc, colors.stone400);
  if (labels.length === 1) {
    const w = doc.getTextWidth(labels[0]);
    doc.text(labels[0], singlePointX - w / 2, baselineY + 9);
  } else {
    doc.text(labels[0], rect.x, baselineY + 9);
    const lastLabel = labels[labels.length - 1];
    doc.text(lastLabel, rect.x + rect.width - doc.getTextWidth(lastLabel), baselineY + 9);
  }
}

/** A right-aligned "label — value" list, e.g. month names beside their counts. */
export interface DataListItem {
  label: string;
  value: string;
  highlight?: boolean; // e.g. the current/most recent month
}

export function drawDataList(
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  items: DataListItem[],
): void {
  let cursorY = y;
  items.forEach((item) => {
    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(8);
    setText(doc, colors.stone500);
    doc.text(item.label, x, cursorY);

    doc.setFont(fonts.mono, 'bold');
    setText(doc, item.highlight ? colors.accent : colors.stone900);
    const valueWidth = doc.getTextWidth(item.value);
    doc.text(item.value, x + width - valueWidth, cursorY);

    cursorY += 15;
  });
}

/** A simple stat tile: small uppercase label + one bold number. No YTD/MTD structure. */
export interface StatCardOptions {
  rect: Rect;
  label: string;
  value: string;
  /** Short caption below the value, e.g. "Requires urgent response". */
  description?: string;
  labelColor?: RGB;
  valueColor?: RGB;
  descriptionColor?: RGB;
  fillColor?: RGB;
  icon?: IconDrawFn;
  iconColor?: RGB; // defaults to stone400 — only used with the vector `icon`
  /** Real icon image as a base64 data URI — rendered as a left-side badge,
   * vertically centered against the whole card. Takes priority over
   * `icon` if both are given. */
  iconImage?: string;
}

const STAT_CARD_LABEL_MAX_FONT_SIZE = 7.5;
const STAT_CARD_LABEL_MIN_FONT_SIZE = 6;
const STAT_CARD_LABEL_PREFERRED_MAX_LINES = 1;
const STAT_CARD_PAD_X = 12;
export const STAT_CARD_ICON_RESERVED_WIDTH = 16; // iconSize(11) + gap(5) — export so callers can pre-measure consistently
export const STAT_CARD_ICON_IMAGE_SIZE = 36;
export const STAT_CARD_ICON_IMAGE_RESERVED_WIDTH = 48; // imageSize(36) + gap(12) — export so callers can pre-measure consistently

export interface StatCardLabelMeasurement {
  lines: string[];
  fontSize: number;
  lineHeight: number;
}

/**
 * Measures label text for a stat card, shrinking the font size (down to a
 * 6pt floor) before adding more wrapped lines — an 8-word label at the
 * default 7.5pt would wrap to 3+ lines and look cramped; shrinking first
 * keeps most long labels to 2 lines. Call this for every card in a row
 * BEFORE drawing any of them, take the smallest resulting fontSize and
 * the max line count across the row, and size/font every card in that
 * row the same way — otherwise one long label shrinks/wraps while its
 * neighbors stay at default size, breaking the row's alignment.
 *
 * Pass `iconReserved` (use STAT_CARD_ICON_RESERVED_WIDTH) whenever the
 * card will have an icon, so wrapping accounts for the narrower text
 * column — mismatched measurement vs. draw-time icon presence causes the
 * pre-computed row height to be wrong.
 */
export function measureStatCardLabel(
  doc: jsPDF,
  label: string,
  width: number,
  iconReserved = 0,
): StatCardLabelMeasurement {
  const maxWidth = width - STAT_CARD_PAD_X * 2 - iconReserved;
  doc.setFont(fonts.body, 'normal');

  let fontSize = STAT_CARD_LABEL_MAX_FONT_SIZE;
  let lines: string[];
  for (;;) {
    doc.setFontSize(fontSize);
    lines = doc.splitTextToSize(label.toUpperCase(), maxWidth) as string[];
    if (lines.length <= STAT_CARD_LABEL_PREFERRED_MAX_LINES || fontSize <= STAT_CARD_LABEL_MIN_FONT_SIZE) {
      break;
    }
    fontSize -= 0.5;
  }

  return { lines, fontSize, lineHeight: fontSize + 1.5 };
}

/** Total card height needed for a given label measurement (use the
 * smallest fontSize / most lines across a row of cards). */
export function computeStatCardHeight(
  measurement: Pick<StatCardLabelMeasurement, 'lines' | 'lineHeight'>,
  hasDescription: boolean,
): number {
  const top = 16;
  const labelHeight = measurement.lines.length * measurement.lineHeight;
  const gapToValue = 10;
  const valueHeight = 14;
  const bottom = hasDescription ? 24 : 14;
  return top + labelHeight + gapToValue + valueHeight + bottom;
}

export function drawStatCard(doc: jsPDF, opts: StatCardOptions): void {
  const { rect, label, value, description, labelColor, valueColor, descriptionColor, fillColor, icon, iconColor, iconImage } = opts;

  setFill(doc, fillColor ?? colors.stone50);
  doc.roundedRect(rect.x, rect.y, rect.width, rect.height, layout.cardRadius, layout.cardRadius, 'F');

  const padX = rect.x + STAT_CARD_PAD_X;
  const iconReserved = iconImage
    ? STAT_CARD_ICON_IMAGE_RESERVED_WIDTH
    : icon
      ? STAT_CARD_ICON_RESERVED_WIDTH
      : 0;
  const textX = padX + iconReserved;

  const { lines: labelLines, fontSize: labelFontSize, lineHeight } = measureStatCardLabel(
    doc,
    label,
    rect.width,
    iconReserved,
  );

  if (iconImage) {
    const iconY = rect.y + rect.height / 2 - STAT_CARD_ICON_IMAGE_SIZE / 2;
    doc.addImage(iconImage, padX, iconY, STAT_CARD_ICON_IMAGE_SIZE, STAT_CARD_ICON_IMAGE_SIZE);
  } else if (icon) {
    icon(doc, padX, rect.y + 7, 11, iconColor ?? colors.stone400);
  }

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(labelFontSize);
  setText(doc, labelColor ?? colors.stone400);
  labelLines.forEach((line, i) => {
    doc.text(line, textX, rect.y + 16 + i * lineHeight);
  });

  const valueY = rect.y + 16 + labelLines.length * lineHeight + 10;
  doc.setFont(fonts.mono, 'bold');
  doc.setFontSize(17);
  setText(doc, valueColor ?? colors.stone900);
  doc.text(value, textX, valueY);

  if (description) {
    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(7);
    setText(doc, descriptionColor ?? labelColor ?? colors.stone400);
    doc.text(description, textX, valueY + 13);
  }
}

/** Horizontal bar chart: one labeled, colored bar per row with the value right-aligned. */
export interface HorizontalBarItem {
  label: string;
  value: number;
  displayValue: string;
  color: RGB;
}

export interface HorizontalBarChartOptions {
  rect: Rect;
  items: HorizontalBarItem[];
  labelWidth?: number;
}

export function drawHorizontalBarChart(doc: jsPDF, opts: HorizontalBarChartOptions): void {
  const { rect, items, labelWidth = 62 } = opts;
  if (items.length === 0) return;

  const max = Math.max(...items.map((i) => i.value), 1);
  const rowHeight = rect.height / items.length;
  const barHeight = Math.min(14, rowHeight - 6);
  const valueColWidth = 40;
  const barMaxWidth = rect.width - labelWidth - valueColWidth - 8;

  items.forEach((item, i) => {
    const rowY = rect.y + i * rowHeight + rowHeight / 2 - barHeight / 2;

    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(8.5);
    setText(doc, colors.stone700);
    doc.text(item.label, rect.x, rowY + barHeight / 2 + 3);

    const barX = rect.x + labelWidth;
    const barWidth = Math.max(2, (item.value / max) * barMaxWidth);

    setFill(doc, colors.stone50);
    doc.roundedRect(barX, rowY, barMaxWidth, barHeight, 2, 2, 'F');
    setFill(doc, item.color);
    doc.roundedRect(barX, rowY, barWidth, barHeight, 2, 2, 'F');

    doc.setFont(fonts.mono, 'bold');
    doc.setFontSize(8.5);
    setText(doc, colors.stone900);
    const valueWidth = doc.getTextWidth(item.displayValue);
    doc.text(item.displayValue, rect.x + labelWidth + barMaxWidth + 8 + (valueColWidth - 8 - valueWidth), rowY + barHeight / 2 + 3);
  });
}

/** Small inline legend: a row of colored swatches each followed by a label. */
export function drawLegend(
  doc: jsPDF,
  x: number,
  y: number,
  items: { color: RGB; label: string; shape?: 'square' | 'circle' }[],
): void {
  let cursorX = x;
  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(7.5);
  items.forEach((item) => {
    setFill(doc, item.color);
    if (item.shape === 'circle') {
      doc.circle(cursorX + 2, y - 2, 2, 'F');
    } else {
      doc.roundedRect(cursorX, y - 5, 5, 5, 0.5, 0.5, 'F');
    }
    setText(doc, colors.stone500);
    doc.text(item.label, cursorX + 8, y);
    cursorX += 8 + doc.getTextWidth(item.label) + 10;
  });
}